import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { Button, Flex, Modal } from "antd"
import type { ColumnsType } from "antd/es/table"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import type { ColumnConfigItem } from "../../containers/User/redux/ui-preferences.types.ts"
import { unavailableProps } from "../../utils/unavailable-control.ts"
import { ActionTooltip } from "../ActionTooltip.tsx"
import { reorderConfig } from "../EntityTable/entity-table.utils"
import { SortableColumnRow } from "./SortableColumnRow"
import { columnLabelText } from "./column-label-text.ts"

interface Props {
  open: boolean
  title: string
  availableColumns: ColumnsType<any>
  currentConfig: ColumnConfigItem[]
  onSave: (next: ColumnConfigItem[]) => void
  onReset: () => void
  onClose: () => void
}

export const TableColumnConfigModal: React.FC<Props> = ({
  open,
  title,
  availableColumns,
  currentConfig,
  onSave,
  onReset,
  onClose
}) => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<ColumnConfigItem[]>([])

  useEffect(() => {
    if (open) {
      setDraft(currentConfig.map((c) => ({ ...c })))
    }
  }, [open, currentConfig])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  )

  const labelByKey = useMemo(() => {
    const map = new Map<string, string>()
    for (const col of availableColumns) {
      const k = col.key === undefined || col.key === null ? "" : String(col.key)
      if (!k) continue
      map.set(k, columnLabelText(col.title, k))
    }
    return map
  }, [availableColumns])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setDraft((curr) => reorderConfig(curr, String(active.id), String(over.id)))
  }

  const handleToggle = (key: string, next: boolean) => {
    setDraft((curr) =>
      curr.map((c) => (c.key === key ? { ...c, visible: next } : c))
    )
  }

  const hasVisibleColumn = draft.some((c) => c.visible)

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      footer={
        <Flex justify="space-between" align="center">
          <Button
            onClick={() => {
              onReset()
              onClose()
            }}
          >
            Reset to defaults
          </Button>
          <Flex gap={8}>
            <Button onClick={onClose}>Cancel</Button>
            <ActionTooltip
              title={hasVisibleColumn ? "" : t("entityTable.atLeastOneColumn")}
            >
              <Button
                type="primary"
                {...unavailableProps(!hasVisibleColumn, () => {
                  onSave(draft)
                  onClose()
                })}
              >
                Save
              </Button>
            </ActionTooltip>
          </Flex>
        </Flex>
      }
      destroyOnHidden
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={draft.map((d) => d.key)}
          strategy={verticalListSortingStrategy}
        >
          {draft.map((item) => (
            <SortableColumnRow
              key={item.key}
              id={item.key}
              label={labelByKey.get(item.key) ?? item.key}
              visible={item.visible}
              onToggle={(next) => handleToggle(item.key, next)}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Modal>
  )
}
